import { useRouter } from 'next/navigation';
import React from 'react';

import {
  ListCardCategories,
  ListCardsCategories,
} from '../../../helpers/list-page/interfaces/list-card.interface';
import { getTenantCardCategories } from '../../../helpers/list-page/list-page-helpers';
import {
  pageIsService,
  pageIsServiceSubCategory,
} from '../../../helpers/list-page/type-guards';
import { ILibraryCuisineType } from '../../../lib/fragments/libraryCuisineType';
import { ILibrarySocialMediaLinks } from '../../../lib/fragments/librarySocialMediaLinks';
import { IMeetingplacePageHomeMinifiedExtendedOpeningHours } from '../../../lib/fragments/meetingPlacePageHome-minified';
import { IMeetingPlacePageServiceItem } from '../../../lib/fragments/meetingPlacePageServiceItem';
import { IMeetingPlacePageTenantEntertainmentItem } from '../../../lib/fragments/meetingPlacePageTenantEntertainmentItem';
import { IMeetingPlacePageTenantFoodBevItem } from '../../../lib/fragments/meetingPlacePageTenantFoodBevItem';
import { IMeetingPlacePageTenantShopItem } from '../../../lib/fragments/meetingPlacePageTenantShopItem';
import { OpeningHours } from '../../opening-hours/opening-hours';
import { RichText } from '../../rich-text/rich-text';
import { SocialMediaLinksList } from '../../social-media-links-list/social-media-links-list';
import { useGetLibraryConstantsDictionaryData } from '@/providers/ConfigProvider';

interface TenantInfo {
  node_locale: string;
  homepage: IMeetingplacePageHomeMinifiedExtendedOpeningHours;
  page:
    | IMeetingPlacePageTenantShopItem
    | IMeetingPlacePageTenantEntertainmentItem
    | IMeetingPlacePageTenantFoodBevItem
    | IMeetingPlacePageServiceItem;
  isMobile: boolean;
  variant?: Variant;
  parentPage?: {
    name: string;
    slug: string;
  };
}

type Variant = 'topInfo' | 'bottomInfo' | 'categories';

interface TenantInfoMobile extends TenantInfo {
  isMobile: true;
  variant?: Variant;
}

interface TenantInfoDesktop extends TenantInfo {
  isMobile: false;
}

export const TenantInfo = ({
  page,
  node_locale,
  homepage,
  variant,
  isMobile,
  parentPage,
}: TenantInfoMobile | TenantInfoDesktop) => {
  const router = useRouter();
  const isService = pageIsService(page) || pageIsServiceSubCategory(page);
  // const isService = false;
  const dictionary = useGetLibraryConstantsDictionaryData();
  let categories: ListCardsCategories[] = [];
  if (!isService) {
    categories = getTenantCardCategories(page);
  }

  const socialMediaLinks: ILibrarySocialMediaLinks | undefined = isService
    ? undefined
    : page?.socialMediaLinks
      ? page.socialMediaLinks
      : page.brand?.socialMediaLinks
        ? page.brand?.socialMediaLinks
        : undefined;

  const isFoodBevPage = (
    item: any,
  ): item is IMeetingPlacePageTenantFoodBevItem => {
    return item.__typename === 'ContentfulMeetingPlacePageTenantFoodBevItem';
  };

  const gotoListingPage = (
    item: ILibraryCuisineType | ListCardCategories,
    type: 'category' | 'cuisinetype',
  ) => {
    const parentPagePath = parentPage?.slug || '';
    if (parentPagePath) {
      const encodedItemName = encodeURIComponent(item.name);
      router.push(`${parentPagePath}?${type}=${encodedItemName}`);
    }
  };

  return (
    <div className="text-left text-text-default lg:text-right">
      {(!isMobile || (isMobile && variant === 'categories')) && (
        <div className="mt-4 flex flex-wrap rounded-xl lg:mb-10 lg:mt-0 lg:justify-end">
          {categories.map((category, i) => (
            <span
              key={i}
              className="my-1 mr-1 cursor-pointer rounded-xl bg-background-primary px-4 py-2 text-buttonlabelm first:ml-0 last:mr-0 lg:hover:bg-background-secondary lg:bg-background-primary-light"
              onClick={() => gotoListingPage(category, 'category')}
            >
              {category.name}
            </span>
          ))}
          {page &&
            isFoodBevPage(page) &&
            page.cuisine?.map((cuisine: any) => (
              <span
                key={cuisine.id}
                className="m-1 cursor-pointer rounded-xl bg-background-primary px-4 py-2 text-buttonlabelm first:ml-0 last:mr-0 lg:hover:bg-background-secondary lg:bg-background-primary-light"
                onClick={() => gotoListingPage(cuisine, 'cuisinetype')}
              >
                {cuisine.name}
              </span>
            ))}
        </div>
      )}

      {(!isMobile || (isMobile && variant === 'topInfo')) && (
        <>
          {page.contactDetails && (
            <div className="mb-6 lg:mt-6">
              <RichText richText={page.contactDetails} />
            </div>
          )}
          <div className="bg-background-primary">
            {!isService && (
              <>
                <div>
                  {isMobile && (
                    <h2 className="text-headings text-text-default">
                      {dictionary?.openingHours}
                    </h2>
                  )}
                  <OpeningHours
                    variant="compact"
                    openingHoursClosed={dictionary?.openingHoursClosed ?? ''}
                    openingHoursClosedToday={
                      dictionary?.openingHoursClosedToday ?? ''
                    }
                    openingHoursOpenToday={
                      dictionary?.openingHoursOpenToday ?? ''
                    }
                    openingHours={
                      page.openingHours
                        ? page.openingHours
                        : homepage.meetingPlace.openingHours
                    }
                    openingHoursExceptions={
                      homepage.meetingPlace.openingHoursExceptions
                    }
                    timezone={homepage.meetingPlace.timezone}
                    node_locale={node_locale}
                  />
                </div>
                <div className="mt-8 mb-8">
                  <OpeningHours
                    variant="tenant-exceptions"
                    openingHoursClosed={dictionary?.openingHoursClosed ?? ''}
                    openingHoursClosedToday={
                      dictionary?.openingHoursClosedToday ?? ''
                    }
                    openingHoursOpenToday={
                      dictionary?.openingHoursOpenToday ?? ''
                    }
                    openingHours={
                      page?.openingHours
                        ? page?.openingHours
                        : homepage.meetingPlace.openingHours
                    }
                    openingHoursExceptions={
                      page?.openingHoursExceptionsCollection?.items
                    }
                    timezone={homepage?.meetingPlace?.timezone}
                    node_locale={node_locale}
                    showMore={dictionary?.showMore ?? ''}
                    showLess={dictionary?.showLess ?? ''}
                  />
                </div>
              </>
            )}
          </div>
        </>
      )}
      {(!isMobile || (isMobile && variant === 'bottomInfo')) && !isService && (
        <>
          {socialMediaLinks && (
            <div className="mt-6">
              <SocialMediaLinksList
                cssClass="flex justify-center lg:justify-end"
                itemCssClass="ml-4"
                socialMediaLinks={socialMediaLinks}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

